import SearchIcon from '@mui/icons-material/Search';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Container, Divider,
    Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {useCallback, useState} from 'react';

import PageTitle from '../components/PageTitle';
import Scrollbar from '../components/scrollbar';
import {GeneralLayout} from '../layouts/GeneralLayout';
import {useLazyGetScheduleDiagnosticsQuery} from '../store/employee/employeeApi';

const STATUS = {
    success: {label: 'Проверено', color: 'success'},
    warning: {label: 'Требует внимания', color: 'warning'},
    error: {label: 'Блокирует загрузку', color: 'error'},
};

const CHECK_CONTENT_SX = {py: 1.5, '&:last-child': {pb: 1.5}};

function toInputDate(date) {
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

function Check({check}) {
    const status = STATUS[check.status] || STATUS.warning;
    const cardSx = {borderLeft: 4, borderLeftColor: `${status.color}.main`};
    return <Card variant="outlined" sx={cardSx}>
        <CardContent sx={CHECK_CONTENT_SX}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1} justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle2">{check.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{check.source}</Typography>
                </Box>
                <Chip size="small" color={status.color} label={status.label}/>
            </Stack>
            <Typography variant="body2" mt={1}>{check.details}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Что сделать: {check.suggestedAction}
            </Typography>
        </CardContent>
    </Card>;
}

function DetailsTable({title, rows, columns}) {
    if (!rows?.length) return null;
    return <Box>
        <Typography variant="subtitle2" mb={1}>{title}</Typography>
        <Scrollbar>
            <TableContainer><Table size="small" sx={{minWidth: 650}}>
                <TableHead><TableRow>{columns.map(column => <TableCell key={column.key}>{column.label}</TableCell>)}</TableRow></TableHead>
                <TableBody>{rows.map((row, index) => <TableRow key={`${row.disciplineName}-${row.groupName}-${index}`} hover>
                    {columns.map(column => <TableCell key={column.key}>{row[column.key] ?? '—'}</TableCell>)}
                </TableRow>)}</TableBody>
            </Table></TableContainer>
        </Scrollbar>
    </Box>;
}

export default function TeacherScheduleDiagnosticsPage() {
    const [fullName, setFullName] = useState('');
    const [date, setDate] = useState(toInputDate(new Date()));
    const [result, setResult] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [runDiagnostics, {isFetching}] = useLazyGetScheduleDiagnosticsQuery();

    const submit = useCallback(async () => {
        if (!fullName.trim()) return;
        try {
            const response = await runDiagnostics({fullName: fullName.trim(), date}).unwrap();
            setResult(response.data);
            setHasSearched(true);
        } catch (error) {
            setResult(null);
            setHasSearched(false);
            alert('Не удалось выполнить диагностику. Повторите попытку позже.');
        }
    }, [date, fullName, runDiagnostics]);

    return <GeneralLayout>
        <PageTitle title="Диагностика расписания и нагрузки преподавателя"/>
        <Container>
            <Stack direction="column" padding="120px 0 40px 100px" spacing={2}>
                <Box>
                    <Typography variant="h4">Диагностика расписания и нагрузки</Typography>
                    <Typography color="text.secondary" mt={1}>
                        Проверяет цепочку: Мегабаза → Sync → FillGroupFlowBinding → внутренняя БД → API расписания 1С.
                    </Typography>
                </Box>
                <Card>
                    <CardContent>
                        <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                            <TextField fullWidth label="ФИО преподавателя" value={fullName}
                                       onChange={event => setFullName(event.target.value)}
                                       onKeyDown={event => event.key === 'Enter' && submit()}/>
                            <TextField label="Дата, на которую ищем занятие" type="date" value={date}
                                       onChange={event => setDate(event.target.value)} InputLabelProps={{shrink: true}}/>
                            <Button variant="contained" onClick={submit} disabled={isFetching || !fullName.trim()}
                                    startIcon={isFetching ? <CircularProgress size={18} color="inherit"/> : <SearchIcon/>}
                                    sx={{minWidth: 170}}>
                                Проверить
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                {hasSearched && result?.employees?.length === 0 && <Alert severity="info">
                    Преподаватель не найден во внутренней БД. Проверьте ФИО или загрузку персонала из Мегабазы.
                </Alert>}

                {result?.employees?.map(employee => <Card key={employee.personMasterGuid}>
                    <CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="h6">{employee.fullName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Проверка на {new Date(result.date).toLocaleDateString('ru-RU')}, неделя {result.weekNumber}
                                </Typography>
                            </Box>
                            <Stack spacing={1}>{employee.checks.map(check => <Check key={check.code} check={check}/>)}</Stack>
                            <Collapse in={employee.workloads?.length > 0 || employee.lessons?.length > 0}>
                                <Divider sx={{my: 2}}/>
                                <Stack spacing={2}>
                                    <DetailsTable title="Привязки нагрузки, созданные FillGroupFlowBinding" rows={employee.workloads}
                                                  columns={[{key: 'groupName', label: 'Группа'}, {key: 'disciplineName', label: 'Дисциплина'}, {key: 'lessonTypeName', label: 'Тип'}, {key: 'chairName', label: 'Кафедра'}, {key: 'hours', label: 'Часы'}]}/>
                                    <DetailsTable title="Найденные занятия" rows={employee.lessons}
                                                  columns={[{key: 'startsAt', label: 'Когда'}, {key: 'groupName', label: 'Группа'}, {key: 'disciplineName', label: 'Дисциплина'}, {key: 'lessonType', label: 'Тип'}]}/>
                                </Stack>
                            </Collapse>
                        </Stack>
                    </CardContent>
                </Card>)}
            </Stack>
        </Container>
    </GeneralLayout>;
}
